<!-- eslint-disable vue/no-setup-props-destructure -->
<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import {
  ElCheckbox,
  ElDatePicker,
  ElInput,
  ElOption,
  ElRadioButton,
  ElRadioGroup,
  ElSelect,
  ElTag,
} from 'element-plus'
import { Search } from '@element-plus/icons-vue'
import { useDebounceFn } from '@vueuse/core'
import { Icon } from '@iconify/vue'
import VisitDetails from '~/components/VisitDetails.vue'
import VisitsList from '~/components/VisitsList.vue'
import { useVisitsStore } from '~/stores/visits'
import { useLocationsStore } from '~/stores/locations'
import { getTerminalLabel } from '~/constants'
import type { VisitStatus } from '~/services/apiClientGps'
import type { TerminalName } from '~/services/apiClient'

useHead({
  title: 'Dray Dog - Tracking',
})

// Router
const route = useRoute()
const router = useRouter()

// This ref prevents the child components (VisitsList, VisitDetails) from
// mounting until the filters have been initialized from the URL. This is
// critical to prevent a race condition where the children would trigger an API
// call before the filters were ready.
const filtersInitialized = ref(false)

// Map style state
const mapStyle = ref<'streets' | 'satellite'>('satellite')

// Get stores first
const visitsStore = useVisitsStore()
const locationsStore = useLocationsStore()
const { locationOptions, loading: loadingLocations } =
  storeToRefs(locationsStore)
const { selectedVisit } = storeToRefs(visitsStore)

// Track if we should show the initial selected visit tag (only from URL on first load)
const showInitialSelectedVisitTag = ref(false)

// Helper to convert terminal names to location IDs
const terminalNamesToIds = (terminalNames: string[]): string[] => {
  if (!locationOptions.value || terminalNames.length === 0) return []

  return terminalNames
    .map((name) => {
      const option = locationOptions.value.find((opt) => opt.label === name)
      return option?.value
    })
    .filter(Boolean) as string[]
}

// Helper to convert location IDs to terminal names
const locationIdsToTerminalNames = (locationIds: string[]): string[] => {
  if (!locationOptions.value || locationIds.length === 0) return []

  return locationIds
    .map((id) => {
      const option = locationOptions.value.find((opt) => opt.value === id)
      return option?.label
    })
    .filter(Boolean) as string[]
}

// Helper to normalize input by splitting on multiple separators and rejoining with commas
const normalizeFilterInput = (input: string): string => {
  if (!input) return ''

  // Split on comma, space, or line break, then filter out empty strings and rejoin with commas
  return input
    .split(/[,\s\n\r]+/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .join(',')
}

// Reads filters from the current URL's query parameters
const initializeFiltersFromUrl = (shouldMapTerminalNames = true) => {
  const query = route.query

  // Get terminal names from URL
  const terminalNames = query.terminals
    ? (query.terminals as string).split(',')
    : []

  // Convert terminal names to location IDs only if requested and locationOptions is available
  let selectedLocationIds: string[] = []
  if (shouldMapTerminalNames && terminalNames.length > 0) {
    selectedLocationIds = terminalNamesToIds(terminalNames)
  }

  return {
    searchTerms: (query.search as string) || '',
    containerNumbers: (query.containers as string) || '',
    selectedLocationIds,
    terminalNames, // Keep the original terminal names for later mapping
    statusFilter: (query.status as 'all' | VisitStatus) || 'all',
    delayedFilter: query.delayed === 'true',
    missingTransactionFilter: query.missingTransaction === 'true',
    selectedVisitId: query.selectedVisitId
      ? Number(query.selectedVisitId)
      : undefined,
    dateRange:
      query.startDate && query.endDate
        ? ([
            new Date(query.startDate as string),
            new Date(query.endDate as string),
          ] as [Date, Date])
        : undefined,
  }
}

// Filter states are initialized from the URL to ensure the UI matches the URL on load
// We don't map terminal names to IDs yet since locationOptions isn't loaded
const initialFilters = initializeFiltersFromUrl(false)
const searchTerms = ref(initialFilters.searchTerms)
const containerNumbers = ref(initialFilters.containerNumbers)
const selectedLocationIds = ref<string[]>([]) // Will be populated after locations load
const statusFilter = ref<'all' | VisitStatus>(initialFilters.statusFilter)
const delayedFilter = ref(initialFilters.delayedFilter)
const missingTransactionFilter = ref(initialFilters.missingTransactionFilter)
const dateRange = ref<[Date, Date] | undefined>(initialFilters.dateRange)

// Helper to format the UI's filter state into the object the API expects
const formatFiltersForApi = (
  uiFilters: Pick<
    ReturnType<typeof initializeFiltersFromUrl>,
    | 'searchTerms'
    | 'containerNumbers'
    | 'selectedLocationIds'
    | 'statusFilter'
    | 'delayedFilter'
    | 'missingTransactionFilter'
    | 'dateRange'
  >,
  includeSelectedVisitId?: number
) => {
  let startDate: string | undefined
  let endDate: string | undefined

  if (uiFilters.dateRange && uiFilters.dateRange.length === 2) {
    const formatLocalDate = (date: Date): string => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    startDate = formatLocalDate(uiFilters.dateRange[0])
    endDate = formatLocalDate(uiFilters.dateRange[1])
  }

  return {
    search: uiFilters.searchTerms || undefined,
    containerNumbers:
      normalizeFilterInput(uiFilters.containerNumbers) || undefined,
    locationIds: uiFilters.selectedLocationIds.join(',') || undefined,
    visitIds: includeSelectedVisitId
      ? String(includeSelectedVisitId)
      : undefined,
    status:
      uiFilters.statusFilter === 'all'
        ? undefined
        : (uiFilters.statusFilter as VisitStatus),
    startDate,
    endDate,
    delayed: uiFilters.delayedFilter || undefined,
    missingTransaction: uiFilters.missingTransactionFilter || undefined,
  }
}

// Syncs the current UI filter state to the URL query parameters
const updateUrl = () => {
  const query: Record<string, string> = {}

  if (searchTerms.value) query.search = searchTerms.value
  if (containerNumbers.value) query.containers = containerNumbers.value

  // Convert location IDs to terminal names for the URL
  if (selectedLocationIds.value.length) {
    const terminalNames = locationIdsToTerminalNames(selectedLocationIds.value)
    if (terminalNames.length) {
      query.terminals = terminalNames.join(',')
    }
  }

  if (statusFilter.value !== 'all') query.status = statusFilter.value
  if (delayedFilter.value) query.delayed = 'true'
  if (missingTransactionFilter.value) query.missingTransaction = 'true'
  if (selectedVisit.value?.id)
    query.selectedVisitId = String(selectedVisit.value.id)

  if (dateRange.value && dateRange.value.length === 2) {
    const formatLocalDate = (date: Date): string => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    query.startDate = formatLocalDate(dateRange.value[0])
    query.endDate = formatLocalDate(dateRange.value[1])
  }

  router.replace({
    path: route.path,
    query: Object.keys(query).length ? query : undefined,
  })
}

// A single debounced function to handle all filter UI changes.
// It updates the URL and then triggers the API call via the store.
const updateFilters = useDebounceFn(() => {
  updateUrl()
  visitsStore.setFilters(
    formatFiltersForApi({
      searchTerms: searchTerms.value,
      containerNumbers: containerNumbers.value,
      selectedLocationIds: selectedLocationIds.value,
      statusFilter: statusFilter.value,
      delayedFilter: delayedFilter.value,
      missingTransactionFilter: missingTransactionFilter.value,
      dateRange: dateRange.value,
    })
  )
}, 500)

// Triggers the initial API call based on current filter state.
const applyInitialFilters = (selectedVisitId?: number) => {
  visitsStore.setFilters(
    formatFiltersForApi(
      {
        searchTerms: searchTerms.value,
        containerNumbers: containerNumbers.value,
        selectedLocationIds: selectedLocationIds.value,
        statusFilter: statusFilter.value,
        delayedFilter: delayedFilter.value,
        missingTransactionFilter: missingTransactionFilter.value,
        dateRange: dateRange.value,
      },
      selectedVisitId
    )
  )
}

// Component Lifecycle: onMounted
onMounted(async () => {
  // 1. Fetch auxiliary data (like terminal locations for the dropdown)
  await locationsStore.loadLocations()

  // 2. Now that locations are loaded, map terminal names from URL to location IDs
  const urlFilters = initializeFiltersFromUrl(true)
  if (urlFilters.terminalNames && urlFilters.terminalNames.length > 0) {
    selectedLocationIds.value = urlFilters.selectedLocationIds
  }

  // Set flag to show initial selected visit tag if there's a selectedVisitId from URL
  if (urlFilters.selectedVisitId) {
    showInitialSelectedVisitTag.value = true
  }

  // 3. Apply filters from the URL. This action calls `setFilters` in the store,
  // which resets the visits list and triggers the initial `loadMore` API call.
  applyInitialFilters(urlFilters.selectedVisitId)

  // 4. Now that filters are set and the first API call is in flight,
  // we can allow the child components to mount.
  filtersInitialized.value = true

  // 4. If the URL specifies a visit to select, we must wait for the API
  // call to finish and populate the `visits` list. A watcher is a robust
  // way to handle this asynchronous operation.
  const stopWatching = watch(
    () => visitsStore.visits,
    (newVisits) => {
      if (newVisits.length > 0) {
        const urlFilters = initializeFiltersFromUrl()
        if (urlFilters.selectedVisitId) {
          const visitToSelect = newVisits.find(
            (v) => v.id === urlFilters.selectedVisitId
          )
          if (visitToSelect) {
            visitsStore.selectVisit(visitToSelect)
          }
        }
        // The watcher has served its purpose, so we remove it to prevent
        // it from running again on subsequent data loads (e.g., infinite scroll).
        stopWatching()
      }
    },
    // `immediate: true` ensures this runs once right away, in case the visits
    // data is already present in the store (e.g., from a previous navigation).
    { deep: true, immediate: true }
  )
})

// Watch for the selected visit to change and update the URL
watch(
  () => selectedVisit.value?.id,
  (newVisitId, oldVisitId) => {
    if (newVisitId !== oldVisitId) {
      updateUrl()
    }
  }
)

// Watch for URL changes (e.g., browser back/forward buttons) and sync the UI
watch(
  () => route.query,
  () => {
    // Only map terminal names if locationOptions is available
    const shouldMapTerminalNames =
      locationOptions.value && locationOptions.value.length > 0
    const newFilters = initializeFiltersFromUrl(shouldMapTerminalNames)

    // Check if the filters in the URL are different from the current UI state
    const filtersChanged =
      searchTerms.value !== newFilters.searchTerms ||
      containerNumbers.value !== newFilters.containerNumbers ||
      JSON.stringify(selectedLocationIds.value) !==
        JSON.stringify(newFilters.selectedLocationIds) ||
      statusFilter.value !== newFilters.statusFilter ||
      delayedFilter.value !== newFilters.delayedFilter ||
      missingTransactionFilter.value !== newFilters.missingTransactionFilter ||
      JSON.stringify(dateRange.value) !== JSON.stringify(newFilters.dateRange)

    const selectedVisitChanged =
      newFilters.selectedVisitId !== selectedVisit.value?.id

    if (filtersChanged) {
      // If they are, update the UI to reflect the URL
      searchTerms.value = newFilters.searchTerms
      containerNumbers.value = newFilters.containerNumbers
      selectedLocationIds.value = newFilters.selectedLocationIds
      statusFilter.value = newFilters.statusFilter
      delayedFilter.value = newFilters.delayedFilter
      missingTransactionFilter.value = newFilters.missingTransactionFilter
      dateRange.value = newFilters.dateRange

      // Then, trigger a new API call with these filters.
      // We don't call `updateUrl` here because we are reacting to a URL change.
      visitsStore.setFilters(formatFiltersForApi(newFilters))
    }

    // Separately handle changes to the selected visit
    if (selectedVisitChanged) {
      if (newFilters.selectedVisitId) {
        const visitToSelect = visitsStore.visits.find(
          (v) => v.id === newFilters.selectedVisitId
        )
        if (visitToSelect) {
          visitsStore.selectVisit(visitToSelect)
        }
      } else {
        visitsStore.clearSelection()
      }
    }
  }
)

// Handle removing the selected visit
const handleRemoveSelectedVisit = () => {
  // Hide the tag and don't show it again
  showInitialSelectedVisitTag.value = false

  // Clear the selected visit from the store
  visitsStore.clearSelection()

  // Update the URL to remove the selectedVisitId parameter
  const query = { ...route.query }
  delete query.selectedVisitId

  router.replace({
    path: route.path,
    query: Object.keys(query).length ? query : undefined,
  })

  // Update filters to remove visitIds and trigger new API call
  visitsStore.setFilters(
    formatFiltersForApi({
      searchTerms: searchTerms.value,
      containerNumbers: containerNumbers.value,
      selectedLocationIds: selectedLocationIds.value,
      statusFilter: statusFilter.value,
      delayedFilter: delayedFilter.value,
      missingTransactionFilter: missingTransactionFilter.value,
      dateRange: dateRange.value,
    })
    // Note: not passing selectedVisitId, so visitIds will be undefined
  )
}

// Date picker shortcuts
const shortcuts = [
  {
    text: 'Last week',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
      return [start, end]
    },
  },
  {
    text: 'Last month',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
      return [start, end]
    },
  },
  {
    text: 'Last 3 months',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
      return [start, end]
    },
  },
]
</script>

<template>
  <div class="flex flex-col h-[calc(100vh-9rem)] gap-4">
    <!-- Filters Section -->
    <div class="bg-white p-4 rounded-md shadow-sm border border-gray-200">
      <!-- First row of filters -->
      <div class="flex flex-row items-center justify-start gap-4 mb-4">
        <ElInput
          v-model="searchTerms"
          placeholder="Search by Truck ID, Truck name, or License Plate (comma-separated)"
          class="!w-180"
          clearable
          size="large"
          @update:model-value="updateFilters"
        >
          <template #prepend>
            <ElIcon><Search /></ElIcon>
          </template>
        </ElInput>

        <p class="text-gray-500">or</p>

        <ElInput
          v-model="containerNumbers"
          placeholder="Search by Container Numbers (comma, space, or line break separated)"
          class="!w-150"
          clearable
          size="large"
          @update:model-value="updateFilters"
        >
          <template #prepend>
            <ElIcon><i-mdi:package-variant /></ElIcon>
          </template>
        </ElInput>

        <p class="text-gray-500">or</p>

        <ElSelect
          v-model="selectedLocationIds"
          class="!w-65"
          size="large"
          multiple
          filterable
          clearable
          collapse-tags
          collapse-tags-tooltip
          :max-collapse-tags="1"
          placeholder="Terminal"
          aria-label="Terminal"
          :loading="loadingLocations"
          @change="updateFilters"
        >
          <ElOption
            v-for="option in locationOptions"
            :key="option.value"
            :label="getTerminalLabel(option.label as TerminalName)"
            :value="option.value"
          />
        </ElSelect>
      </div>

      <!-- Second row of filters -->
      <div class="flex flex-row items-center justify-start gap-6">
        <ElRadioGroup
          v-model="statusFilter"
          size="large"
          @change="updateFilters"
        >
          <ElRadioButton value="all" size="large">
            <Icon icon="mdi:view-list" class="filter-icon mr-2 -my-2px" />
            <span class="mr-2">All</span>
          </ElRadioButton>
          <ElRadioButton value="in_progress" size="large">
            <Icon icon="mdi:clock-outline" class="filter-icon mr-2 -my-2px" />
            <span class="mr-2">In Progress</span>
          </ElRadioButton>
          <ElRadioButton value="completed" size="large">
            <Icon
              icon="mdi:check-circle-outline"
              class="filter-icon mr-2 -my-2px"
            />
            <span class="mr-2">Completed</span>
          </ElRadioButton>
        </ElRadioGroup>

        <div class="flex gap-4">
          <ElCheckbox
            v-model="delayedFilter"
            label="Delayed"
            size="large"
            @change="updateFilters"
          />
          <ElCheckbox
            v-model="missingTransactionFilter"
            label="Missing Transaction"
            size="large"
            @change="updateFilters"
          />
        </div>

        <div class="flex items-center gap-2">
          <ElDatePicker
            v-model="dateRange"
            type="daterange"
            unlink-panels
            range-separator="To"
            start-placeholder="Start date"
            end-placeholder="End date"
            :shortcuts="shortcuts"
            size="large"
            @change="updateFilters"
          />
          <ElTag
            v-if="showInitialSelectedVisitTag && selectedVisit?.id"
            closable
            size="default"
            round
            effect="dark"
            @close="handleRemoveSelectedVisit"
          >
            Selected Visit: {{ selectedVisit.id }}
          </ElTag>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div v-if="filtersInitialized" class="flex h-[calc(100%-6rem)]">
      <!-- Left Column - Visits List -->
      <div
        class="min-w-[40em] mr-2 overflow-hidden flex flex-col border border-gray-200 rounded-md shadow-sm"
      >
        <div class="flex-1 overflow-auto">
          <VisitsList />
        </div>
      </div>

      <!-- Right Column -->
      <div class="min-w-[40em] flex-1 flex-col">
        <VisitDetails v-model="mapStyle" />
      </div>
    </div>
  </div>
</template>

<style>
/* Custom scrollbar styles */
.overflow-auto {
  scrollbar-width: thin;
  scrollbar-color: rgba(203, 213, 225, 0.4) transparent;
}

.overflow-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-auto::-webkit-scrollbar-thumb {
  background-color: rgba(203, 213, 225, 0.4);
  border-radius: 3px;
}

.overflow-auto::-webkit-scrollbar-thumb:hover {
  background-color: rgba(203, 213, 225, 0.6);
}

:deep(.el-input-number .el-input__wrapper) {
  padding-left: 0;
}

:deep(.el-select .el-input__wrapper) {
  box-shadow: 0 0 0 1px #e5e7eb;
}

:deep(.el-select .el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #d1d5db;
}

:deep(.el-input .el-input__wrapper) {
  box-shadow: 0 0 0 1px #e5e7eb;
}

:deep(.el-input .el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #d1d5db;
}
</style>

<route lang="yaml">
meta:
  auth: true
name: tracking
</route>
